import classNames from 'classnames';
import type {
  ComponentProps,
  HTMLAttributes,
  MouseEvent,
  MouseEventHandler,
} from 'react';
import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import type { DistributedOmit } from 'type-fest';
import type { CaretDirection } from './Caret';
import Caret from './Caret';
import PopOver from './deprecated/PopOver';
import * as styles from './Menu.module.scss';
import Slot from './Slot';
import SVGIcon from './SVGIcon';
import Tether from './Tether';
import type { TetherRef } from './Tether';
import Tooltip from './Tooltip';
import addTestId from '../utils/addTestId';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import Link from '../utils/router/Link';
import trackMouseLeaveIntent from '../utils/trackMouseLeaveIntent';
import useHover from '../utils/useHover';

export type MenuItemElement = HTMLDivElement & HTMLAnchorElement;

interface MenuItemCaretProps {
  direction: CaretDirection;
}

const MenuItemCaret = ({ direction }: MenuItemCaretProps) => (
  <div aria-hidden="true" role="img" className={styles.caret}>
    <Caret direction={direction} size="small" />
  </div>
);

type BaseMenuItemProps = {
  children: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  tooltip?: string;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  wrap?: boolean;
} & (
  | {
      to: React.ComponentProps<typeof Link>['to'];
      state?: React.ComponentProps<typeof Link>['state'];
      newTab?: React.ComponentProps<typeof Link>['newTab'];
    }
  | { to?: never; state?: never; newTab?: never }
) &
  Pick<
    HTMLAttributes<HTMLElement>,
    | 'aria-expanded'
    | 'aria-checked'
    | 'aria-haspopup'
    | 'aria-selected'
    | 'className'
    | 'id'
    | 'role'
    | 'onClick'
    | 'onMouseEnter'
    | 'onMouseLeave'
  >;

const BaseMenuItem = forwardRef<MenuItemElement, BaseMenuItemProps>(
  (
    {
      children,
      danger = false,
      disabled = false,
      icon,
      right,
      tooltip,
      tooltipPlacement = 'left',
      wrap = false,
      to,
      state,
      newTab,
      ...props
    },
    ref,
  ) => {
    const commonProps = {
      ...props,
      ref,
      'aria-disabled': disabled ?? undefined,
      children: (
        <div className={styles.content}>
          {!!icon && (
            <Slot aria-hidden="true" role="img">
              {typeof icon === 'string' ? (
                <SVGIcon className={styles.icon}>{icon}</SVGIcon>
              ) : (
                <div className={styles.icon}>{icon}</div>
              )}
            </Slot>
          )}
          <div
            className={classNames(styles.main, {
              [styles.wrap]: wrap,
            })}
          >
            {children}
          </div>
          {!!right && <div className={styles.right}>{right}</div>}
        </div>
      ),
      className: classNames(styles.item, props.className, {
        [styles.danger]: danger,
      }),
      tabIndex: -1,
    } as const satisfies Partial<
      ComponentProps<typeof Link> & HTMLAttributes<HTMLDivElement>
    >;

    const content = to ? (
      // eslint-disable-next-line jsx-a11y/anchor-has-content -- we're using children
      <Link {...commonProps} to={to} state={state} newTab={newTab} />
    ) : (
      <div {...commonProps} />
    );
    return tooltip ? (
      <Tooltip placement={tooltipPlacement} content={tooltip} display="block">
        {content}
      </Tooltip>
    ) : (
      content
    );
  },
);

type MenuItemRadioProps = DistributedOmit<BaseMenuItemProps, 'right'> & {
  checked?: boolean;
  testId?: string;
  onClick?: MouseEventHandler;
};

export const MenuItemRadio = React.forwardRef<
  MenuItemElement,
  MenuItemRadioProps
>(({ id, checked, testId, onClick, ...props }: MenuItemRadioProps, ref) => (
  <BaseMenuItem
    ref={ref}
    id={id}
    role="menuitemradio"
    aria-checked={checked}
    right={<div aria-hidden="true" className={styles.checkmark} />}
    onClick={onClick}
    {...props}
    {...addTestId(testId)}
  />
));

type MenuItemProps = DistributedOmit<BaseMenuItemProps, 'right'> & {
  testId?: string;
  onClick?: MouseEventHandler;
} & (
    | { submenuItems?: React.ReactNode; right?: never }
    | { submenuItems?: never; right?: BaseMenuItemProps['right'] }
  );

/**
 * A normal menu item that can have a submenu.
 *
 * @example
 * <Menu>
 *   <MenuItem onClick={() => {}}>Action</MenuItem>
 *   <MenuItem to="/path">Navigation</MenuItem>
 *   <MenuItem submenuItems={<>
 *     <MenuItem>Submenu item 1</MenuItem>
 *     <MenuItem>Submenu item 2</MenuItem>
 *   </>}>
 *     Submenu
 *   </MenuItem>
 * </Menu>
 */
export const MenuItem = React.forwardRef<MenuItemElement, MenuItemProps>(
  ({ submenuItems, testId, onClick, ...props }: MenuItemProps, ref) => {
    const [isSubmenuExpanded, setIsSubmenuExpanded] = useState(false);
    const isMobile = useIsSize(Sizes.SM);

    const propsForBase = {
      ...addTestId(testId),
      role: 'menuitem',
      onClick,
      ...props,
    } satisfies Partial<BaseMenuItemProps>;

    if (!submenuItems) {
      return <BaseMenuItem ref={ref} {...propsForBase} />;
    }

    const propsForMenuItemWithSubmenu = {
      ...propsForBase,
      submenuExpanded: isSubmenuExpanded,
      onSubmenuExpandedChange: setIsSubmenuExpanded,
      'aria-expanded': isSubmenuExpanded,
      'aria-haspopup': 'menu',
    } satisfies Partial<MenuItemWithSubmenuProps>;

    return isMobile ? (
      <MobileMenuItemWithSubmenu
        ref={ref}
        {...propsForMenuItemWithSubmenu}
        submenuItems={submenuItems}
      />
    ) : (
      <DesktopMenuItemWithSubmenu
        ref={ref}
        {...propsForMenuItemWithSubmenu}
        submenuItems={submenuItems}
      />
    );
  },
);

type MenuItemWithSubmenuProps = BaseMenuItemProps & {
  submenuItems: React.ReactNode;
  submenuExpanded: boolean;
  onSubmenuExpandedChange: (isExpanded: boolean) => void;
};

const DesktopMenuItemWithSubmenu = React.forwardRef<
  MenuItemElement,
  MenuItemWithSubmenuProps
>(
  (
    {
      submenuItems,
      submenuExpanded,
      onSubmenuExpandedChange,
      className,
      onClick,
      ...props
    },
    ref,
  ) => {
    const submenuId = useId();

    // When a submenu is open, disable other menu items to prevent them from being
    // highlighted as the mouse moves across them while navigating to the submenu.
    const [shouldDisableOtherMenuItems, setShouldDisableOtherMenuItems] =
      useState(false);

    const cleanUpMouseLeaveTrackingRef = useRef(() => {});
    useEffect(() => {
      return () => {
        cleanUpMouseLeaveTrackingRef.current();
      };
    }, []);

    const {
      onMouseEnter: originalOnMouseEnter,
      onMouseLeave: originalOnMouseLeave,
      setHover,
    } = useHover({
      hoverDelay: 'fast',
      hoverGroupId: 'menu',
      onHoverChange(hoveringId) {
        onSubmenuExpandedChange(hoveringId === submenuId);
      },
    });

    const tetherRef = useRef<TetherRef>(null);

    const onMouseEnter = (event: MouseEvent) => {
      cleanUpMouseLeaveTrackingRef.current();
      originalOnMouseEnter(submenuId, event);
    };

    const onMouseLeave = (event: MouseEvent) => {
      cleanUpMouseLeaveTrackingRef.current();

      const handleIntentionalMouseLeave = () => {
        originalOnMouseLeave(submenuId);
        setShouldDisableOtherMenuItems(false);
      };

      if (tetherRef.current?.positionedElement) {
        cleanUpMouseLeaveTrackingRef.current = trackMouseLeaveIntent({
          initialMousePosition: { x: event.clientX, y: event.clientY },
          targetRect:
            tetherRef.current.positionedElement.getBoundingClientRect(),
          onLeave: handleIntentionalMouseLeave,
        });
      } else {
        handleIntentionalMouseLeave();
      }
    };

    const expandedProps = {
      ...props,
      className: classNames(className, {
        [styles.disableOtherMenuItems]: shouldDisableOtherMenuItems,
      }),
      right: <MenuItemCaret direction="right" />,
      onMouseEnter: (event) => {
        setShouldDisableOtherMenuItems(true);
        onMouseEnter(event);
      },
      onMouseLeave,
      onClick: (e) => {
        e.stopPropagation();
        onClick?.(e);
      },
    } satisfies Partial<BaseMenuItemProps>;

    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(
      null,
    );

    return (
      <>
        <Slot ref={setAnchorElement}>
          <BaseMenuItem ref={ref} {...expandedProps} />
        </Slot>
        <Tether
          ref={tetherRef}
          anchorElement={anchorElement}
          placement="left-start"
          offset={5}
          positionedView={
            submenuExpanded && (
              <div
                className={styles.submenuContainer}
                onMouseEnter={(event) => {
                  setShouldDisableOtherMenuItems(false);
                  onMouseEnter(event);
                }}
                onMouseLeave={onMouseLeave}
                onClick={() => setHover(null)}
              >
                <Menu>{submenuItems}</Menu>
              </div>
            )
          }
        />
      </>
    );
  },
);

const MobileMenuItemWithSubmenu = React.forwardRef<
  MenuItemElement,
  MenuItemWithSubmenuProps
>(
  (
    {
      submenuItems,
      submenuExpanded,
      onSubmenuExpandedChange,
      className,
      onClick,
      ...props
    },
    ref,
  ) => {
    const expandedProps = {
      ...props,
      className: classNames(className, styles.isMobile),
      right: <MenuItemCaret direction={submenuExpanded ? 'up' : 'down'} />,
      onClick: (e) => {
        e.stopPropagation();
        onClick?.(e);
        onSubmenuExpandedChange(!submenuExpanded);
      },
    } satisfies Partial<BaseMenuItemProps>;

    return (
      <>
        <BaseMenuItem ref={ref} {...expandedProps} />
        {submenuExpanded && (
          <div className={styles.expandedSubmenuItems}>{submenuItems}</div>
        )}
      </>
    );
  },
);

interface MenuSimpleItemProps {
  children: React.ReactNode;
}

export const MenuSimpleItem = ({ children }: MenuSimpleItemProps) => (
  <div className={styles.simpleItem}>{children}</div>
);

interface MenuSeparatorProps {
  fullWidth?: boolean;
}

export const MenuSeparator = ({ fullWidth = false }: MenuSeparatorProps) => (
  <div
    role="separator"
    className={classNames(styles.separator, {
      [styles.fullWidth]: fullWidth,
    })}
  />
);

interface MenuSectionProps {
  children: React.ReactNode;
  header: string;
}

/**
 * Group a list of menu items together with a header.
 *
 * @example
 * <Menu>
 *   <MenuSection header="Group 1">
 *     <MenuItem>Item 1</MenuItem>
 *     <MenuItem>Item 2</MenuItem>
 *   </MenuSection>
 * </Menu>
 */
export const MenuSection = ({ children, header }: MenuSectionProps) => (
  <section role="group" className={styles.section}>
    <header role="presentation" className={styles.sectionHeader}>
      {header}
    </header>
    {children}
  </section>
);
interface MenuProps {
  children: React.ReactNode;
  ariaLabel?: string;
  style?: React.CSSProperties;
}

const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ children, ariaLabel, style }, ref) =>
    children ? (
      <PopOver.Container noPadding width="auto" fullHeight>
        <div
          ref={ref}
          role="menu"
          data-no-print
          className={styles.menu}
          style={style}
          aria-label={ariaLabel}
        >
          {children}
        </div>
      </PopOver.Container>
    ) : null,
);

export default Menu;
